import { useMutation } from '@apollo/client';
import { updateExperimentSettings } from 'api/mutations/experiments';
import { experimentEditDataLoaded } from 'modules/experiments/takeover/ducks/actions/experimentActions';
import { SaveExperimentSettingsResponse, UpdateExperimentsArgs } from 'modules/experiments/types';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useExperimentsTranslations } from '../useTranslations';

const source = 'useUpdateExperimentSettings';

export const useUpdateExperimentingSettings = ({ experimentId }: { experimentId: number }) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const errorMessage = `Failed to save settings for experiment id ${experimentId}`;
  const errorToasterMessage = t('settings.errors.unableToSave');
  const successToasterMessage = t('successMessage.experimentSettings');

  const errorHandler = (error: unknown) => {
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage,
          source,
          error,
        }),
      ),
    );
  };

  const [updateExpSettings] = useMutation(updateExperimentSettings, {
    onError: errorHandler,
  });

  return async (
    updateExperiment: UpdateExperimentsArgs,
    locale: string,
  ): Promise<SaveExperimentSettingsResponse> => {
    const { data } = await updateExpSettings({
      variables: { ...updateExperiment, ...(locale && { locale }) },
    });

    if (data?.updateExperimentSettings?.id) {
      dispatch(experimentEditDataLoaded(data.updateExperimentSettings));
      dispatch(addPositiveToast(successToasterMessage));
      return {
        isSuccess: true,
        data: data.updateExperimentSettings,
      };
    }

    if (data?.updateExperimentSettings?.message) {
      errorHandler(data.updateExperimentSettings.message);
    }

    return {
      isSuccess: false,
    };
  };
};
